import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal, unstable_batchedUpdates } from 'react-dom';
import {
  useDndContext,
  // CancelDrop,
  // closestCenter,
  // pointerWithin,
  // rectIntersection,
  // CollisionDetection,
  DndContext,
  DragOverlay,
  // DropAnimation,
  // getFirstCollision,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  // Modifiers,
  useDraggable,
  useDroppable,
  // UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy,
  // KeyboardCoordinateGetter,
  defaultDropAnimationSideEffects,
  useDndMonitor
} from '@dnd-kit/core';
import {
  // AnimateLayoutChanges,
  SortableContext,
  useSortable,
  // arrayMove,
  // defaultAnimateLayoutChanges,
  verticalListSortingStrategy,
  // SortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';

export { arrayMove } from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';
export function WrappedDndContext({ onDragStart, onDragOver, onDragEnd, onDragCancel, children }) {
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor)
    // useSensor(KeyboardSensor, {
    //   coordinateGetter,
    // })
  );

  return (
    <DndContext
      sensors={sensors}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      // cancelDrop={cancelDrop}
      onDragCancel={onDragCancel}
    // modifiers={modifiers}
    >
      {children}
    </DndContext>
  );
};

export function WrappedSortableContext({ vertical = true, items, id, children }) {
  return (
    <SortableContext
      id={id}
      items={items}
      strategy={
        vertical
          ? verticalListSortingStrategy
          : horizontalListSortingStrategy
      }
    >
      {children}
    </SortableContext>
  );
};

export function SortHandle({
  id,
  className,
  disabled = false,
  dom = 'div',
  children
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef
  } = useSortable({ id, disabled });
  let handlProps = {
    ...attributes,
    ...listeners,
  }
  let Element = dom
  return <Element ref={setActivatorNodeRef} {...handlProps} className={className}>{children}</Element>;
}

export function SortableItem({
  id,
  className,
  t,
  disabled = false,
  dom = 'div',
  handle = true,
  onClick = () => {},
  onDoubleClick = () => {},
  children
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    disabled,
    data: {
      // tag the data so I can identify the type from rescript
      t: t,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  let props = handle ? { ...attributes, ...listeners } : {};
  let Element = dom
  return (
    <Element ref={setNodeRef} onDoubleClick={onDoubleClick} onClick={onClick} style={style} {...props} className={className}>
      {children}
    </Element>
  );
}

export function DragHandle({
  id,
  className,
  disabled = false,
  dom = 'div',
  children
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef
  } = useDraggable({ id, disabled });
  let handlProps = {
    ...attributes,
    ...listeners,
  }
  let Element = dom
  return <Element ref={setActivatorNodeRef} {...handlProps} className={className}>{children}</Element>;
}

export function DraggableItem({
  id,
  className,
  t,
  disabled = false,
  dom = 'div',
  handle = true,
  onClick = () => { },
  onDoubleClick = () => { },
  hideOnDrag = false,
  children
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useDraggable({
    id,
    disabled,
    data: {
      // tag the data so I can identify the type from rescript
      t: t,
    }
  });

  const style = {
    opacity: hideOnDrag && isDragging ? 0 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  let props = handle ? { ...attributes, ...listeners } : {};
  let Element = dom
  return (
    <Element ref={setNodeRef} onDoubleClick={onDoubleClick} onClick={onClick}  style={style} {...props} className={className}>
      {children}
    </Element>
  );
}

export function Droppable({
  id,
  className,
  t,
  disabled = false,
  dom = 'div',
  children
}) {
  const {
    setNodeRef
  } = useDroppable({
    id,
    disabled,
    data: {
      // tag the data so I can identify the type from rescript
      t: t,
    }
  });

  let Element = dom
  return (
    <Element ref={setNodeRef} className={className}>
      {children}
    </Element>
  );
}

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

export function WrappedDragOverlay({
  children
}) {
  return createPortal(
    <DragOverlay dropAnimation={dropAnimation}>
      {children}
    </DragOverlay>,
    document.body
  );
}

export const wrappedUseDndMonitor = useDndMonitor;
export const wrappedUseDndContext = useDndContext;